import { GoGovFooter } from '@/go.gov/components/layout/GoGovFooter';
import GoGovHeaderBar from '@/go.gov/components/layout/GoGovHeaderBar';
import { FavoritesProvider } from '@/ui/Layout/Favorites/FavoritesContext';
import HeaderBar from '@/ui/Layout/Header/HeaderBar';
import {
  SidebarNavigation,
  useSidebarNavigation,
} from '@/ui/Layout/Sidebar/SidebarNavigation';
import { hubThemeLight } from '@/ui/theme';
import { CustomThemeProvider } from '@/ui/theme/ThemeContext';
import { IS_ETS_NEXT } from '@/utils/constants';
import { HubTranslationProvider } from '@/utils/i18n/HubTranslationProvider';
import { PageMetadataProvider } from '@/utils/pageMetadata/PageMetadataContext';
import { Box, useMediaQuery } from '@mui/material';
import { Analytics } from '@vercel/analytics/react';
import { useRouter } from 'next/router';

function Layout({
  children,
  fontClassnames,
  hubUiTranslations,
  hubUiLocale,
  hubUiTranslationScopeKey,
}) {
  const router = useRouter();
  const isMobile = useMediaQuery(`(max-width:699px)`);

  const expandedWidth = 275;
  const collapsedWidth = 60;

  const { isCollapsed, toggleCollapse, expand } = useSidebarNavigation(false);

  const noLayoutUrls = [
    '/login',
    '/login-v2',
    '/_error',
    '/confirm-password-reset',
    '/confirm-password-reset-v2',
    '/finish-registration',
    '/forgot-password',
    '/forgot-password-v2',
    '/privacy-policy',
    '/terms-of-use',
    '/sso',
    '/sso-finish',
  ];

  if (noLayoutUrls.includes(router.pathname)) {
    return (
      <>
        <Box id='nprogress-container' sx={{ width: '100%', height: '3px' }} />
        <Box
          component='main'
          id='main-content'
          data-a11y-main
          tabIndex={-1}
          sx={{ width: '100%' }}
          flex
          className={fontClassnames}
        >
          {children}
        </Box>
      </>
    );
  }

  return (
    <>
      <PageMetadataProvider>
        <HubTranslationProvider
          initialTranslations={hubUiTranslations ?? undefined}
          initialLocale={hubUiLocale ?? undefined}
          initialScopeKey={hubUiTranslationScopeKey ?? undefined}
        >
          <FavoritesProvider>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                height: '100vh',
                bgcolor: 'white',
              }}
            >
              <Box
                sx={{
                  overflow: 'auto',
                  borderLeft: '1px solid #E0E0E0',
                  zIndex: 1,
                  position: 'relative',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 0,
                  order: 2,
                }}
              >
                {IS_ETS_NEXT ? <GoGovHeaderBar /> : <HeaderBar />}
                <Box
                  id='nprogress-container'
                  sx={{ width: '100%', height: '3px' }}
                />
                <Box
                  component='main'
                  id='main-content'
                  data-a11y-main
                  tabIndex={-1}
                  sx={{ width: '100%', height: '100%', flex: 1 }}
                  className={fontClassnames}
                >
                  {children}
                  {!IS_ETS_NEXT && <Analytics />}
                </Box>
              </Box>

              <CustomThemeProvider
                initialSettings={{ themeOverride: hubThemeLight }}
              >
                <Box sx={{ order: 1 }}>
                  {!isMobile && (
                    <SidebarNavigation
                      isCollapsed={isCollapsed}
                      onToggleCollapse={toggleCollapse}
                      onExpandSidebar={expand}
                      currentPath={router.pathname}
                      width={expandedWidth}
                      collapsedWidth={collapsedWidth}
                      showToggleButton={true}
                      isMobile={false}
                    />
                  )}
                </Box>
              </CustomThemeProvider>
            </Box>
          </FavoritesProvider>
        </HubTranslationProvider>
      </PageMetadataProvider>
      {IS_ETS_NEXT && <GoGovFooter />}
    </>
  );
}

export default Layout;
